import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { Link, Navigate, NavLink, useLocation, useParams, useSearchParams } from "react-router";
import { WaitlistForm } from "../pages/landing.tsx";
import { useMe } from "../session.ts";
import { cx } from "../ui.tsx";
import {
  CUSTOMERS,
  type Entry,
  FAIR_USE,
  INDUSTRIES,
  PRICE,
  PRODUCTS,
  SOLUTIONS,
  VENDORS,
  type Vendor,
} from "./content.ts";
import { type Block, POSTS, type Post, postPath } from "./posts.ts";
import { ClauseCard, CreditMemo, PriceBreakdown } from "./visuals.tsx";

/**
 * The public site. One idea throughout: your contract already promises a credit; recoup finds
 * it (the highlighter), claims it (the credit memo) and charges one simple price. Campaign
 * landing pages (/welcome, /partners) stay separate and focused on the signup form.
 */

const SECTIONS = {
  product: {
    label: "Product",
    path: "/product",
    items: PRODUCTS,
    intro: "What recoup does, part by part.",
  },
  solutions: {
    label: "Solutions",
    path: "/solutions",
    items: SOLUTIONS,
    intro: "The problems recoup solves.",
  },
  industries: {
    label: "Industries",
    path: "/industries",
    items: INDUSTRIES,
    intro: "Where recoup fits.",
  },
  customers: {
    label: "Teams",
    path: "/customers",
    items: CUSTOMERS,
    intro: "The teams that use recoup.",
  },
} as const;
type SectionKey = keyof typeof SECTIONS;

/** The header's menus: few links, each with a line on why you'd open it. */
type MenuLink = [to: string, title: string, why?: string];
const MENUS: { label: string; cols: { title?: string; links: MenuLink[] }[]; more?: MenuLink }[] = [
  {
    label: "Product",
    cols: [
      {
        links: [
          ["/how-it-works", "How it works", "From contract to money back, in three steps"],
          [
            "/product/contract-reader",
            "Contract reader",
            "What each vendor promised, and what a miss is worth",
          ],
          [
            "/product/vendor-watch",
            "Vendor monitoring",
            "Checked against what your contract promised",
          ],
          ["/product/claims", "Claims", "Sent on time, followed until you're paid"],
        ],
      },
    ],
    more: ["/product", "All features"],
  },
  {
    label: "Solutions",
    cols: [
      {
        title: "By team",
        links: [
          ["/customers/finance-leaders", "Finance"],
          ["/customers/procurement", "Procurement"],
          ["/customers/it-and-operations", "IT and operations"],
          ["/customers/pe-operating-partners", "Private equity firms"],
        ],
      },
      {
        title: "By need",
        links: [
          ["/solutions/sla-credit-recovery", "Get back what you're owed"],
          ["/solutions/never-miss-a-deadline", "Never miss a deadline"],
          ["/solutions/vendor-renewals", "Better renewal deals"],
        ],
      },
    ],
    more: ["/industries", "By industry"],
  },
  {
    label: "Resources",
    cols: [
      {
        links: POSTS.slice(0, 3).map((p) => [postPath(p), p.title] as MenuLink),
      },
    ],
    more: ["/resources", "All posts"],
  },
];
const FOOTER: [string, [string, string][]][] = [
  [
    "Product",
    [
      ["/how-it-works", "How it works"],
      ["/product", "Features"],
      ["/pricing", "Pricing"],
      ["/supported-vendors", "Vendors"],
    ],
  ],
  [
    "Solutions",
    [
      ["/customers", "By team"],
      ["/solutions", "By need"],
      ["/industries", "By industry"],
    ],
  ],
  [
    "Resources",
    [
      ["/resources", "Blog"],
      ["/resources/glossary", "Glossary"],
    ],
  ],
];

function usePageMeta(title: string, description: string) {
  useEffect(() => {
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    const before = meta?.getAttribute("content") ?? "";
    meta?.setAttribute("content", description);
    return () => {
      document.title = "recoup";
      meta?.setAttribute("content", before);
    };
  }, [title, description]);
}

const navCls =
  "whitespace-nowrap rounded-chip px-3 py-2 text-sm font-medium text-muted hover:bg-surface-2 hover:text-ink";

function Menu({ m }: { m: (typeof MENUS)[number] }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const loc = useLocation();
  // biome-ignore lint/correctness/useExhaustiveDependencies: close on navigation
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={cx(navCls, "inline-flex items-center gap-1", open && "bg-surface-2 text-ink")}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
      >
        {m.label}
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          aria-hidden
          className={cx("opacity-60 transition-transform", open && "rotate-180")}
        >
          <path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      </button>
      {open && (
        <div
          id={id}
          className={cx(
            "absolute top-full left-0 z-30 mt-2 rounded-card border border-line bg-surface p-2 shadow-pop",
            m.cols.length > 1 ? "w-[30rem]" : "w-80",
          )}
        >
          <div className={cx("grid gap-1", m.cols.length > 1 && "grid-cols-2")}>
            {m.cols.map((c) => (
              <div key={c.title ?? "all"} className="flex min-w-0 flex-col">
                {c.title && <span className="px-3 pt-2 pb-1 eyebrow">{c.title}</span>}
                {c.links.map(([to, t, why]) => (
                  <Link key={to} to={to} className="rounded-chip px-3 py-2 hover:bg-surface-2">
                    <span className="block text-sm font-medium">{t}</span>
                    {why && <span className="block text-xs text-muted">{why}</span>}
                  </Link>
                ))}
              </div>
            ))}
          </div>
          {m.more && (
            <Link
              to={m.more[0]}
              className="mt-1 block rounded-chip border-t border-line px-3 pt-2.5 pb-2 text-sm font-semibold text-accent hover:bg-surface-2"
            >
              {m.more[1]} →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

const PrimaryLink = ({ to, children, big }: { to: string; children: ReactNode; big?: boolean }) => (
  <Link
    to={to}
    className={cx(
      "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-control bg-ink font-semibold text-bg transition-transform hover:-translate-y-0.5",
      big ? "h-12 px-6 text-base" : "h-9 px-4 text-sm",
    )}
  >
    {children}
  </Link>
);

function Header() {
  const { data: me } = useMe();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  // biome-ignore lint/correctness/useExhaustiveDependencies: close on navigation
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);
  const account: [string, string] = me?.user ? ["/", "Open recoup"] : ["/signin", "Sign in"];
  return (
    <header className="sticky top-0 z-20 border-b border-line/70 bg-bg/85 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <Link to="/" className="flex items-center gap-2">
          <img src="/favicon.svg" alt="" className="size-7" />
          <span className="font-display text-xl font-bold tracking-tight">recoup</span>
        </Link>
        <nav className="hidden items-center gap-0.5 lg:flex" aria-label="Main">
          {MENUS.map((m) => (
            <Menu key={m.label} m={m} />
          ))}
          <NavLink
            to="/pricing"
            className={({ isActive }) => cx(navCls, isActive && "bg-surface-2 text-ink")}
          >
            Pricing
          </NavLink>
        </nav>
        <div className="flex items-center gap-1">
          <Link to={account[0]} className={cx(navCls, "hidden sm:block")}>
            {account[1]}
          </Link>
          <PrimaryLink to="/waitlist">Get started</PrimaryLink>
          <button
            type="button"
            className="ml-1 rounded-chip border border-line px-3 py-1.5 text-sm lg:hidden"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            Menu
          </button>
        </div>
      </div>
      {open && (
        <nav
          className="max-h-[75dvh] overflow-y-auto border-t border-line bg-surface lg:hidden"
          aria-label="Main"
        >
          <div className="mx-auto grid max-w-6xl gap-6 px-4 py-5 sm:grid-cols-2 sm:px-6">
            <div className="flex flex-col">
              {(
                [["/pricing", "Pricing"], ["/waitlist", "Get started"], account] as [
                  string,
                  string,
                ][]
              ).map(([to, l]) => (
                <Link key={to + l} to={to} className="py-1.5 font-display text-lg font-semibold">
                  {l}
                </Link>
              ))}
            </div>
            {MENUS.map((m) => (
              <div key={m.label} className="flex flex-col">
                <span className="pb-1 eyebrow">{m.label}</span>
                {[...m.cols.flatMap((c) => c.links), ...(m.more ? [m.more] : [])].map(([to, t]) => (
                  <Link key={to} to={to} className="py-1 text-sm">
                    {t}
                  </Link>
                ))}
              </div>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-band text-band-ink">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-14 sm:grid-cols-2 sm:px-6 lg:grid-cols-[1.6fr_1fr_1fr_1fr]">
        <div className="flex flex-col items-start gap-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/favicon.svg" alt="" className="size-6" />
            <span className="font-display text-lg font-bold">recoup</span>
          </Link>
          <p className="max-w-xs text-sm text-band-muted">
            Your software vendors owe you money. recoup gets it back.
          </p>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/waitlist" className="font-semibold text-hl">
              Get started →
            </Link>
            <Link to="/signin" className="text-band-muted hover:text-band-ink">
              Sign in
            </Link>
          </div>
        </div>
        {FOOTER.map(([h, links]) => (
          <nav key={h} className="flex flex-col gap-1.5 text-sm" aria-label={h}>
            <span className="mb-1 font-mono text-label tracking-label text-band-muted uppercase">
              {h}
            </span>
            {links.map(([to, l]) => (
              <Link key={to} to={to} className="text-band-ink/85 hover:text-band-ink">
                {l}
              </Link>
            ))}
          </nav>
        ))}
      </div>
      <div className="mx-auto max-w-6xl px-4 pb-8 text-xs text-band-muted sm:px-6">
        © {new Date().getFullYear()} recoup. Vendor names belong to their owners and appear only to
        describe contracts recoup can read; no vendor endorses recoup.
      </div>
    </footer>
  );
}

export function SiteLayout({ children }: { children: ReactNode }) {
  const loc = useLocation();
  // biome-ignore lint/correctness/useExhaustiveDependencies: back to the top on each new page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [loc.pathname]);
  return (
    <div className="flex min-h-dvh flex-col">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <Header />
      <main id="main" tabIndex={-1} className="flex-1 focus:outline-none">
        {children}
      </main>
      <Footer />
    </div>
  );
}

const Wrap = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cx("mx-auto max-w-6xl px-4 sm:px-6", className)}>{children}</div>
);

const Kicker = ({ children }: { children: ReactNode }) => (
  <p className="font-mono text-xs font-medium tracking-kicker text-accent uppercase">{children}</p>
);

const Display = ({
  children,
  as: As = "h2",
  className,
}: {
  children: ReactNode;
  as?: "h1" | "h2";
  className?: string;
}) => (
  <As
    className={cx(
      "font-display font-bold tracking-tight text-balance",
      As === "h1"
        ? "text-5xl leading-display sm:text-6xl lg:text-7xl"
        : "text-3xl leading-tight sm:text-4xl",
      className,
    )}
  >
    {children}
  </As>
);

function Cta() {
  return (
    <section className="bg-band text-band-ink">
      <Wrap className="flex flex-col items-start gap-6 py-16 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <h2 className="font-display text-3xl font-bold tracking-tight text-balance sm:text-4xl">
            See what your vendors <span className="hl-word">owe you</span>.
          </h2>
          <p className="mt-3 max-w-xl text-band-muted">
            Start with a free audit: up to 5 contracts and the last 12 months, with what each vendor
            owed you and the claims ready to send.
          </p>
        </div>
        <Link
          to="/waitlist"
          className="inline-flex h-12 shrink-0 items-center rounded-control bg-hl px-6 font-semibold text-hl-ink transition-transform hover:-translate-y-0.5"
        >
          Get started
        </Link>
      </Wrap>
    </section>
  );
}

const STEPS = [
  [
    "Read",
    "Upload your vendor contracts. recoup finds what each vendor promised, what a miss is worth, and the deadline to ask.",
  ],
  ["Monitor", "Every month, recoup checks each vendor against what it promised you."],
  [
    "Claim",
    "When a vendor falls short, recoup writes the claim, sends it on time, and follows it until you're paid.",
  ],
] as const;

/** The product demo: a real walk through recoup, every click shown, from sign-in to a sent claim. */
function DemoVideo() {
  return (
    <figure className="overflow-hidden rounded-panel border border-line bg-band shadow-hero">
      <video
        className="block aspect-[16/9] w-full"
        src="/demo/recoup-demo.mp4"
        poster="/demo/recoup-demo-poster.jpg"
        controls
        playsInline
        preload="none"
      >
        <track kind="captions" />
      </video>
      <figcaption className="sr-only">
        A walk through recoup: sign in, drag in a vendor contract, check what recoup read, approve
        it, see what you are owed, and send a claim.
      </figcaption>
    </figure>
  );
}

function Steps() {
  return (
    <ol className="grid gap-px overflow-hidden rounded-panel border border-line bg-line md:grid-cols-3">
      {STEPS.map(([t, b], i) => (
        <li key={t} className="flex flex-col gap-3 bg-surface p-7">
          <span className="font-mono text-sm text-faint">0{i + 1}</span>
          <h3 className="font-display text-3xl font-bold tracking-tight">{t}</h3>
          <p className="text-muted">{b}</p>
        </li>
      ))}
    </ol>
  );
}

function VendorStrip() {
  return (
    <div className="flex flex-wrap gap-2">
      {VENDORS.map((v) => (
        <Link
          key={v.slug}
          to={`/supported-vendors/${v.slug}`}
          className="rounded-full border border-line bg-surface px-3.5 py-1.5 text-sm transition-colors hover:border-ink"
        >
          {v.name}
        </Link>
      ))}
    </div>
  );
}

function PriceLine() {
  return (
    <p className="font-display text-3xl font-bold tracking-tight text-balance sm:text-4xl">
      <span className="marker">${PRICE.perVendor}</span> per vendor a month, plus{" "}
      <span className="marker">{PRICE.successFee}%</span> of what we recover.
    </p>
  );
}

/** Opens in place: the details belong on this page, not behind another click away. */
function Accordion({ items }: { items: readonly Entry[] }) {
  return (
    <div className="divide-y divide-line border-y border-line">
      {items.map((i) => (
        <details key={i.slug} className="group">
          <summary className="flex cursor-pointer list-none items-baseline justify-between gap-6 py-4 [&::-webkit-details-marker]:hidden">
            <span className="min-w-0">
              <span className="block font-display text-xl font-semibold tracking-tight group-open:text-accent">
                {i.title}
              </span>
              <span className="block text-sm text-muted">{i.summary}</span>
            </span>
            <span
              className="flex size-7 shrink-0 items-center justify-center rounded-full border border-line text-muted transition-transform group-open:rotate-45 group-open:border-ink group-open:text-ink"
              aria-hidden
            >
              +
            </span>
          </summary>
          <div className="flex flex-col gap-5 pb-6">
            <p className="max-w-2xl text-muted">{i.lead}</p>
            {i.sections.map((sec) => (
              <div key={sec.heading} className="min-w-0">
                <h3 className="font-semibold">{sec.heading}</h3>
                {sec.text && <p className="mt-1 max-w-2xl text-muted">{sec.text}</p>}
                {sec.bullets && (
                  <ul className="mt-2 flex flex-col gap-1.5">
                    {sec.bullets.map((b) => (
                      <li key={b} className="flex gap-3">
                        <span className="mt-2 h-1 w-4 shrink-0 rounded-full bg-hl" aria-hidden />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </details>
      ))}
    </div>
  );
}

const checkedOn = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

/** What recoup reads in a vendor's agreement when the vendor doesn't publish one table for everyone. */
function ContractTerms({ v }: { v: Vendor }) {
  const short = v.name.split(" (")[0];
  return (
    <div className="flex flex-col gap-4">
      <p className="max-w-2xl text-muted">
        {short}'s terms depend on the product and plan you bought, so the ones that count are in
        your contract. recoup reads:
      </p>
      <ul className="grid gap-2 sm:grid-cols-2">
        {[
          `The uptime promise for the ${short} products you bought`,
          "What you get back for each miss",
          "The deadline to ask, and where to send the claim",
          "What doesn't count, like planned maintenance",
        ].map((t) => (
          <li key={t} className="flex gap-3">
            <span className="mt-2 h-1 w-4 shrink-0 rounded-full bg-hl" aria-hidden />
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}

function WatchedAt({ v }: { v: Vendor }) {
  return (
    <p className="font-mono text-label text-faint">
      Monitored at{" "}
      <a href={v.status} target="_blank" rel="noreferrer" className="text-accent">
        {v.status.replace(/^https:\/\/(www\.)?/, "")}
      </a>
      , plus your own checks and {v.name.split(" (")[0]}'s incident emails.
    </p>
  );
}

/** Pick a vendor and its terms show right below, so the visitor stays on the page. */
function VendorExplorer() {
  const [slug, setSlug] = useState(VENDORS[0]!.slug);
  const v = VENDORS.find((x) => x.slug === slug)!;
  const p = v.published;
  return (
    <div className="flex flex-col gap-6">
      <fieldset className="m-0 flex min-w-0 flex-wrap gap-2 border-0 p-0">
        <legend className="sr-only">Example vendors</legend>
        {VENDORS.map((x) => (
          <button
            key={x.slug}
            type="button"
            aria-pressed={x.slug === slug}
            onClick={() => setSlug(x.slug)}
            className={cx(
              "rounded-full border px-3.5 py-1.5 text-sm transition-colors",
              x.slug === slug
                ? "border-ink bg-ink font-medium text-bg"
                : "border-line bg-surface hover:border-ink",
            )}
          >
            {x.name}
          </button>
        ))}
      </fieldset>
      <div className="memo rounded-b-card px-6 pt-8 pb-6 shadow-hero" aria-live="polite">
        <div className="flex flex-wrap items-baseline justify-between gap-2 eyebrow">
          <span>
            {v.name} · {v.category}
          </span>
          {p && (
            <a
              href={p.source}
              target="_blank"
              rel="noreferrer"
              className="tracking-normal normal-case text-accent"
            >
              Published SLA ↗
            </a>
          )}
        </div>
        {p ? (
          <>
            <dl className="mt-5 grid gap-x-6 gap-y-3 sm:grid-cols-[140px_1fr]">
              <dt className="font-mono text-xs text-muted uppercase">Promise</dt>
              <dd>
                <mark className="mark-clause">{p.promise}</mark>
              </dd>
              <dt className="font-mono text-xs text-muted uppercase">Credits</dt>
              <dd>{p.credits.join(" · ")}</dd>
              <dt className="font-mono text-xs text-muted uppercase">Deadline</dt>
              <dd>
                <mark className="mark-clause">{p.deadline}</mark>
              </dd>
              <dt className="font-mono text-xs text-muted uppercase">How to claim</dt>
              <dd>{p.howToClaim}</dd>
            </dl>
            {p.note && <p className="mt-4 text-sm text-muted">{p.note}</p>}
            <p className="mt-4 font-mono text-label text-faint">
              As published, checked {checkedOn(p.checked)}. Negotiated contracts often differ;
              recoup uses yours.
            </p>
          </>
        ) : (
          <div className="mt-4">
            <ContractTerms v={v} />
          </div>
        )}
        <div className="mt-4">
          <WatchedAt v={v} />
        </div>
      </div>
    </div>
  );
}

function LinkList({ items, base }: { items: readonly Entry[]; base: string }) {
  return (
    <ul className="divide-y divide-line border-y border-line">
      {items.map((i) => (
        <li key={i.slug}>
          <Link
            to={`${base}/${i.slug}`}
            className="group flex items-baseline justify-between gap-6 py-4"
          >
            <span className="min-w-0">
              <span className="block font-display text-xl font-semibold tracking-tight group-hover:text-accent">
                {i.title}
              </span>
              <span className="block text-sm text-muted">{i.summary}</span>
            </span>
            <span className="text-faint transition-transform group-hover:translate-x-1" aria-hidden>
              →
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------------- pages

export function SiteHome() {
  usePageMeta(
    "recoup: get back what your vendors owe you",
    "When a software vendor misses its uptime promise, your contract often owes you money back. recoup finds it, claims it, and charges one simple price.",
  );
  return (
    <SiteLayout>
      <section className="relative overflow-hidden bg-band text-band-ink">
        <Wrap className="grid items-center gap-14 pt-16 pb-24 lg:grid-cols-[1.25fr_1fr] lg:pt-24">
          <div className="rise min-w-0">
            <p className="font-mono text-xs font-medium tracking-kicker text-hl uppercase">
              SLA credit recovery
            </p>
            <Display as="h1" className="mt-5">
              Your software vendors <span className="hl-word">owe you money</span>. recoup gets it
              back.
            </Display>
            <p className="mt-6 max-w-xl text-lg text-band-muted">
              When a software vendor goes down, your contract usually gives you money off your next
              bill, a service credit. Almost nobody claims it. recoup reads your contracts, monitors
              your vendors and claims it before the deadline, with the math and proof.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-5">
              <Link
                to="/waitlist"
                className="inline-flex h-12 items-center justify-center rounded-control bg-hl px-6 text-base font-semibold whitespace-nowrap text-hl-ink transition-transform hover:-translate-y-0.5"
              >
                Get started
              </Link>
              <Link
                to="/how-it-works"
                className="font-semibold text-band-ink underline decoration-white/30 decoration-2 underline-offset-4 hover:decoration-hl"
              >
                See how it works
              </Link>
            </div>
          </div>
          <CreditMemo />
        </Wrap>
      </section>

      <section className="border-y border-line bg-surface">
        <Wrap className="py-20">
          <Kicker>How it works</Kicker>
          <Display className="mt-4 max-w-2xl">Read. Monitor. Claim.</Display>
          <div className="mt-10">
            <Steps />
          </div>
        </Wrap>
      </section>

      <Wrap className="grid items-center gap-12 py-20 lg:grid-cols-2">
        <div className="min-w-0">
          <Kicker>What we look for</Kicker>
          <Display className="mt-4">Every contract hides the same three lines.</Display>
          <p className="mt-5 max-w-lg text-muted">
            What the vendor promised, what you get back when it misses, and the deadline to ask.
            Most people stop at the first. recoup reads all three, in every contract.
          </p>
          <Link
            to="/resources/read-an-sla-credit-clause"
            className="mt-6 inline-block font-semibold text-accent"
          >
            How to read a credit clause →
          </Link>
        </div>
        <ClauseCard />
      </Wrap>

      <section className="bg-band text-band-ink">
        <Wrap className="grid gap-10 py-20 lg:grid-cols-[1fr_1.4fr] lg:items-center">
          <div className="min-w-0">
            <p className="font-mono text-xs font-medium tracking-kicker text-hl uppercase">
              Pricing
            </p>
            <p className="mt-4 font-display text-3xl font-bold tracking-tight text-balance sm:text-4xl">
              ${PRICE.perVendor} per vendor a month, plus {PRICE.successFee}% of what we recover.
            </p>
            <p className="mt-4 text-band-muted">
              The same price for every company. You start with a free audit, so you see what you're
              owed before you pay anything.
            </p>
            <Link to="/pricing" className="mt-6 inline-block font-semibold text-hl">
              Pricing details →
            </Link>
          </div>
          <PriceBreakdown />
        </Wrap>
      </section>

      <Wrap className="py-20">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
          <div className="min-w-0">
            <Kicker>Who it's for</Kicker>
            <Display className="mt-4">Built for the people who pay the invoices.</Display>
          </div>
          <Accordion items={CUSTOMERS} />
        </div>
      </Wrap>

      <section className="border-t border-line bg-surface">
        <Wrap className="py-16">
          <h2 className="font-display text-2xl font-bold tracking-tight">
            Works with the vendors you already pay
          </h2>
          <p className="mt-2 text-muted">
            Any vendor whose contract promises uptime. Pick one to see what recoup reads:
          </p>
          <div className="mt-6">
            <VendorExplorer />
          </div>
        </Wrap>
      </section>

      <Cta />
    </SiteLayout>
  );
}

export function HowItWorks() {
  usePageMeta(
    "How recoup works",
    "Read your contracts, monitor your vendors, get your money back: how recoup works.",
  );
  const detail: [string, string, string[]][] = [
    [
      "Read",
      "Upload your contracts as PDF, Word or text. recoup lists every promise, in the contract's own words. You check it and approve.",
      [
        "How much uptime, and how fast support answers",
        "The credit for each miss",
        "The deadline to ask, and when the contract renews",
      ],
    ],
    [
      "Monitor",
      "Every month, recoup checks each vendor against your contract, not just the vendor's own status page.",
      [
        "The vendor's status page, for the services you pay for",
        "recoup's own checks on the service",
        "The vendor's outage emails",
      ],
    ],
    [
      "Claim",
      "When a vendor falls short, recoup works out what you're owed, writes the claim with proof, and sends it when you say so.",
      [
        "Every number shown, step by step",
        "Vendor replies read, and pushback answered",
        "Followed until you're paid",
      ],
    ],
  ];
  return (
    <SiteLayout>
      <Wrap className="pt-16 pb-12">
        <Kicker>How it works</Kicker>
        <Display as="h1" className="mt-5 max-w-4xl">
          From your contract to a <span className="marker">credit on your invoice</span>.
        </Display>
        <p className="mt-4 max-w-2xl text-lg text-muted">
          Vendors call it a service credit: money off your next bill, owed when they miss the uptime
          they promised.
        </p>
        <div className="mt-10">
          <DemoVideo />
        </div>
      </Wrap>
      <Wrap className="flex flex-col gap-4 pb-20">
        {detail.map(([t, lead, bullets], i) => (
          <div
            key={t}
            className="grid gap-6 rounded-panel border border-line bg-surface p-7 md:grid-cols-[auto_1fr_1fr] md:items-start"
          >
            <span className="font-mono text-sm text-faint md:pt-3">0{i + 1}</span>
            <div className="min-w-0">
              <h2 className="font-display text-4xl font-bold tracking-tight">{t}</h2>
              <p className="mt-3 text-muted">{lead}</p>
            </div>
            <ul className="flex flex-col gap-2.5 md:pt-2">
              {bullets.map((b) => (
                <li key={b} className="flex gap-3">
                  <span className="mt-2 h-1 w-4 shrink-0 rounded-full bg-hl" aria-hidden />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </Wrap>
      <section className="border-y border-line bg-surface">
        <Wrap className="grid items-center gap-12 py-20 lg:grid-cols-2">
          <div className="min-w-0">
            <Display>One example, start to finish.</Display>
            <p className="mt-5 max-w-lg text-muted">
              On October 21, Snowflake's AWS Europe (Ireland) region was down for 5 hours, which
              left the month at 99.32%. Snowflake's own SLA pays a day of usage back below 99.9%, if
              you ask within 21 days. For a company spending $31,000 a month there, that's $1,000,
              and recoup claims it before November 21.
            </p>
          </div>
          <CreditMemo />
        </Wrap>
      </section>
      <Wrap className="grid gap-10 py-20 md:grid-cols-3">
        {[
          [
            "You approve what goes out",
            "Nothing goes to a vendor unless you say so. You can also let small claims send on their own, after a wait you choose.",
          ],
          [
            "Your data stays yours",
            "Every account signs in with a second step, and each company's data stays private to that company. Single sign-on is included.",
          ],
          [
            "Nothing to install",
            "recoup works from your contracts and public information. It never needs access to your systems.",
          ],
        ].map(([t, b]) => (
          <div key={t}>
            <h3 className="font-display text-xl font-semibold tracking-tight">{t}</h3>
            <p className="mt-2 text-muted">{b}</p>
          </div>
        ))}
      </Wrap>
      <Cta />
    </SiteLayout>
  );
}

export function Pricing() {
  usePageMeta(
    "recoup pricing",
    `A free audit, then $${PRICE.perVendor} per vendor a month plus ${PRICE.successFee}% of credits recovered.`,
  );
  const faq: [string, string][] = [
    [
      "What's a vendor?",
      "A company you pay that recoup monitors, like AWS or Atlassian. It counts once, however many files you upload for it.",
    ],
    [
      "Do I pay for vendors that owe me nothing?",
      "Yes, $4 a month. recoup still monitors them and keeps their track record for your next renewal.",
    ],
    [
      "When is the 15% charged?",
      "Only when money recoup claimed actually comes off your bill. If nothing comes back, you pay nothing extra.",
    ],
    [
      "When can I start?",
      "Today. Tell us about your company and we'll set up your account and your free audit.",
    ],
    [
      "Can I stop after the free audit?",
      "Yes. There's nothing to sign, and the claims it wrote are yours to send.",
    ],
    [
      "Do large companies pay differently?",
      "No. Everyone pays the same rates. Larger companies can pay once a year by invoice instead of by card.",
    ],
  ];
  return (
    <SiteLayout>
      <Wrap className="pt-16 pb-14">
        <Kicker>Pricing</Kicker>
        <Display as="h1" className="mt-5 max-w-4xl">
          Start with a free audit. Pay only for the vendors we monitor.
        </Display>
        <div className="mt-10 max-w-3xl">
          <PriceLine />
        </div>
      </Wrap>
      <Wrap className="grid gap-4 pb-16 md:grid-cols-2">
        <div className="rounded-panel border border-line bg-surface p-7">
          <span className="font-mono text-sm text-faint">First</span>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight">Free audit</h2>
          <p className="mt-3 text-muted">
            Up to {PRICE.auditContracts} contracts and the last 12 months: what each vendor owed
            you, with the math and claims ready for anything still in time.
          </p>
          <p className="mt-5 font-display text-4xl font-bold">$0</p>
        </div>
        <div className="rounded-panel border-2 border-ink bg-surface p-7">
          <span className="font-mono text-sm text-faint">Then</span>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight">Recover</h2>
          <p className="mt-3 text-muted">
            Every vendor monitored. Every claim written, sent and followed. Deadline and renewal
            reminders, single sign-on, everything included.
          </p>
          <p className="mt-5 font-display text-4xl font-bold">
            ${PRICE.perVendor}
            <span className="text-base font-medium text-muted">
              {" "}
              per vendor a month + {PRICE.successFee}%
            </span>
          </p>
        </div>
      </Wrap>
      <Wrap className="grid gap-12 py-20 lg:grid-cols-2">
        <div className="min-w-0">
          <h2 className="font-display text-2xl font-bold tracking-tight">
            The fine print, in full
          </h2>
          <ul className="mt-6 flex flex-col gap-3">
            {FAIR_USE.map((f) => (
              <li key={f} className="flex gap-3">
                <span className="mt-2 h-1 w-4 shrink-0 rounded-full bg-hl" aria-hidden />
                {f}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm text-faint">You see your price before you pay anything.</p>
        </div>
        <dl className="flex flex-col gap-6">
          {faq.map(([q, a]) => (
            <div key={q}>
              <dt className="font-semibold">{q}</dt>
              <dd className="mt-1 text-muted">{a}</dd>
            </div>
          ))}
        </dl>
      </Wrap>
      <Cta />
    </SiteLayout>
  );
}

export function GetStarted() {
  usePageMeta(
    "Get started · recoup",
    "Start with a free audit: see what your software vendors owe you.",
  );
  const [params] = useSearchParams();
  const audience = params.get("for") === "partner" ? "partner" : "company";
  return (
    <SiteLayout>
      <Wrap className="grid gap-12 py-16 lg:grid-cols-[1fr_1.1fr]">
        <div className="min-w-0">
          <Kicker>Get started</Kicker>
          <Display as="h1" className="mt-5">
            See what you're <span className="marker">owed</span>.
          </Display>
          <ol className="mt-10 flex flex-col gap-6">
            {[
              ["Tell us about your company", "Free. Two minutes, with your work email."],
              [
                "Get your account",
                "We set up your recoup account and email you a link to sign in.",
              ],
              [
                "Get your free audit",
                `Send up to ${PRICE.auditContracts} contracts. See what each vendor owed you in the last 12 months.`,
              ],
            ].map(([t, b], i) => (
              <li key={t} className="flex gap-5">
                <span className="font-mono text-sm text-faint">0{i + 1}</span>
                <div className="min-w-0">
                  <h2 className="font-display text-2xl font-bold tracking-tight">{t}</h2>
                  <p className="text-muted">{b}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div className="min-w-0 lg:sticky lg:top-24 lg:self-start">
          <WaitlistForm audience={audience} />
        </div>
      </Wrap>
    </SiteLayout>
  );
}

export function SectionIndex({ k }: { k: SectionKey }) {
  const s = SECTIONS[k];
  usePageMeta(`${s.label} · recoup`, s.intro);
  return (
    <SiteLayout>
      <Wrap className="py-16">
        <Kicker>{s.label}</Kicker>
        <Display as="h1" className="mt-5 max-w-3xl">
          {s.intro}
        </Display>
        <div className="mt-12">
          <Accordion items={s.items} />
        </div>
      </Wrap>
      <Cta />
    </SiteLayout>
  );
}

export function EntryPage({ k }: { k: SectionKey }) {
  const s = SECTIONS[k];
  const { slug } = useParams();
  const e = (s.items as readonly Entry[]).find((i) => i.slug === slug);
  usePageMeta(e ? `${e.title} · recoup` : "recoup", e?.summary ?? s.intro);
  if (!e) return <Navigate to={s.path} replace />;
  const others = (s.items as readonly Entry[]).filter((i) => i.slug !== e.slug);
  return (
    <SiteLayout>
      <Wrap className="py-16">
        <nav
          className="font-mono text-xs tracking-label text-faint uppercase"
          aria-label="Breadcrumb"
        >
          <Link to={s.path} className="hover:text-ink">
            {s.label}
          </Link>
        </nav>
        <Display as="h1" className="mt-5 max-w-4xl">
          {e.title}
        </Display>
        <p className="mt-6 max-w-2xl text-xl text-muted">{e.lead}</p>
        {e.sections.length > 0 && (
          <div className="mt-14 grid gap-x-12 gap-y-10 md:grid-cols-2">
            {e.sections.map((sec) => (
              <div key={sec.heading} className="border-t-2 border-ink pt-5">
                <h2 className="font-display text-2xl font-bold tracking-tight">{sec.heading}</h2>
                {sec.text && <p className="mt-3 text-muted">{sec.text}</p>}
                {sec.bullets && (
                  <ul className="mt-4 flex flex-col gap-2.5">
                    {sec.bullets.map((b) => (
                      <li key={b} className="flex gap-3">
                        <span className="mt-2 h-1 w-4 shrink-0 rounded-full bg-hl" aria-hidden />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </Wrap>
      <section className="border-t border-line bg-surface">
        <Wrap className="py-16">
          <h2 className="mb-6 font-display text-2xl font-bold tracking-tight">
            More {s.label.toLowerCase()}
          </h2>
          <LinkList items={others} base={s.path} />
        </Wrap>
      </section>
      <Cta />
    </SiteLayout>
  );
}

export function VendorsIndex() {
  usePageMeta(
    "Vendors recoup works with · recoup",
    "Any software or cloud vendor whose contract promises uptime.",
  );
  const groups = [...new Set(VENDORS.map((v) => v.category))];
  return (
    <SiteLayout>
      <Wrap className="py-16">
        <Kicker>Vendors</Kicker>
        <Display as="h1" className="mt-5 max-w-4xl">
          If the contract promises uptime, recoup can read it.
        </Display>
        <p className="mt-6 max-w-2xl text-lg text-muted">
          These are just examples. recoup works with any vendor. Where a vendor publishes its terms,
          we show them here. Your own contract may differ, and that's the one recoup uses.
        </p>
        <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((g) => (
            <div key={g} className="min-w-0 border-t-2 border-ink pt-4">
              <h2 className="eyebrow">{g}</h2>
              <ul className="mt-3 flex flex-col">
                {VENDORS.filter((v) => v.category === g).map((v) => (
                  <li key={v.slug}>
                    <Link
                      to={`/supported-vendors/${v.slug}`}
                      className="flex items-center gap-2 py-1.5 font-medium hover:text-accent"
                    >
                      {v.name}
                      {v.published && (
                        <span className="mark-clause font-mono text-label">public terms</span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Wrap>
      <Cta />
    </SiteLayout>
  );
}

export function VendorPage() {
  const { slug } = useParams();
  const v: Vendor | undefined = VENDORS.find((x) => x.slug === slug);
  usePageMeta(
    v ? `${v.name} SLA credits · recoup` : "recoup",
    v ? `How recoup claims SLA credits from ${v.name}.` : "",
  );
  if (!v) return <Navigate to="/supported-vendors" replace />;
  const p = v.published;
  const short = v.name.split(" (")[0];
  return (
    <SiteLayout>
      <Wrap className="py-16">
        <nav
          className="font-mono text-xs tracking-label text-faint uppercase"
          aria-label="Breadcrumb"
        >
          <Link to="/supported-vendors" className="hover:text-ink">
            Vendors
          </Link>{" "}
          · {v.category}
        </nav>
        <Display as="h1" className="mt-5 max-w-4xl">
          {v.name} SLA credits
        </Display>
        <p className="mt-6 max-w-2xl text-lg text-muted">
          recoup reads the contract you signed with {short}, monitors the services it covers and
          claims any credit owed before the deadline.
        </p>
        {p ? (
          <div className="memo mt-12 max-w-3xl rounded-b-card px-7 pt-9 pb-7 shadow-hero">
            <div className="flex flex-wrap items-baseline justify-between gap-2 eyebrow">
              <span>Published terms</span>
              <a
                href={p.source}
                target="_blank"
                rel="noreferrer"
                className="normal-case tracking-normal text-accent"
              >
                {p.source.replace("https://", "")} ↗
              </a>
            </div>
            <dl className="mt-5 grid gap-4 sm:grid-cols-[150px_1fr]">
              <dt className="font-mono text-xs text-muted uppercase">Promise</dt>
              <dd>
                <mark className="mark-clause">{p.promise}</mark>
              </dd>
              <dt className="font-mono text-xs text-muted uppercase">Credits</dt>
              <dd>
                <ul className="flex flex-col gap-1">
                  {p.credits.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </dd>
              <dt className="font-mono text-xs text-muted uppercase">Deadline</dt>
              <dd>
                <mark className="mark-clause">{p.deadline}</mark>
              </dd>
              <dt className="font-mono text-xs text-muted uppercase">How to claim</dt>
              <dd>{p.howToClaim}</dd>
            </dl>
            {p.note && <p className="mt-5 text-sm text-muted">{p.note}</p>}
            <p className="mt-5 font-mono text-label text-faint">
              As published, checked {checkedOn(p.checked)}. Negotiated contracts often differ;
              recoup uses yours.
            </p>
            <div className="mt-2">
              <WatchedAt v={v} />
            </div>
          </div>
        ) : (
          <div className="mt-12 max-w-3xl border-t-2 border-ink pt-5">
            <h2 className="font-display text-2xl font-bold tracking-tight">
              What recoup reads in your {short} agreement
            </h2>
            <div className="mt-4">
              <ContractTerms v={v} />
            </div>
            <div className="mt-5">
              <WatchedAt v={v} />
            </div>
          </div>
        )}
      </Wrap>
      <section className="border-t border-line bg-surface">
        <Wrap className="py-16">
          <h2 className="mb-6 font-display text-2xl font-bold tracking-tight">Other vendors</h2>
          <VendorStrip />
        </Wrap>
      </section>
      <Cta />
    </SiteLayout>
  );
}

// ---------------------------------------------------------------------------------- blog

const longDate = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

function PostMeta({ p }: { p: Post }) {
  return (
    <p className="font-mono text-xs tracking-label text-faint uppercase">
      {longDate(p.date)} · {p.minutes} min read · The recoup team
    </p>
  );
}

/** Share a post as itself: the link carries its own title and summary in the preview. */
function ShareBar({ p }: { p: Post }) {
  const [copied, setCopied] = useState(false);
  const url = `${location.origin}${postPath(p)}`;
  const enc = encodeURIComponent;
  const linkCls =
    "inline-flex h-9 items-center rounded-control border border-line bg-surface px-3.5 text-sm font-medium hover:border-ink";
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="mr-1 eyebrow">Share</span>
      <a
        className={linkCls}
        target="_blank"
        rel="noreferrer"
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`}
      >
        LinkedIn
      </a>
      <a
        className={linkCls}
        target="_blank"
        rel="noreferrer"
        href={`https://x.com/intent/post?url=${enc(url)}&text=${enc(p.title)}`}
      >
        X
      </a>
      <button
        type="button"
        className={linkCls}
        onClick={() => {
          navigator.clipboard?.writeText(url).then(
            () => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1600);
            },
            () => {},
          );
        }}
      >
        {copied ? "Link copied" : "Copy link"}
      </button>
    </div>
  );
}

function PostBody({ blocks }: { blocks: Block[] }) {
  return (
    <div className="flex flex-col gap-5 text-prose">
      {blocks.map((b) => {
        const k = JSON.stringify(b).slice(0, 80);
        if ("h" in b)
          return (
            <h2 key={k} className="mt-6 font-display text-2xl font-bold tracking-tight">
              {b.h}
            </h2>
          );
        if ("p" in b) return <p key={k}>{b.p}</p>;
        if ("note" in b)
          return (
            <p
              key={k}
              className="rounded-card border border-line bg-surface px-5 py-4 text-base text-muted"
            >
              {b.note}
            </p>
          );
        if ("ul" in b || "ol" in b) {
          const items = "ul" in b ? b.ul : b.ol;
          return (
            <ul key={k} className="flex flex-col gap-2.5">
              {items.map((t, j) => (
                <li key={t} className="flex gap-3">
                  {"ol" in b ? (
                    <span className="tabular mt-1 flex size-6 shrink-0 items-center justify-center rounded-full bg-hl font-mono text-xs font-semibold text-hl-ink">
                      {j + 1}
                    </span>
                  ) : (
                    <span className="mt-3 h-1 w-4 shrink-0 rounded-full bg-hl" aria-hidden />
                  )}
                  <span className="min-w-0">{t}</span>
                </li>
              ))}
            </ul>
          );
        }
        return (
          <div key={k} className="overflow-x-auto rounded-card border border-line bg-surface">
            <table className="w-full text-left text-base">
              <thead className="border-b border-line bg-surface-2 font-mono text-label tracking-label text-muted uppercase">
                <tr>
                  {b.head.map((h) => (
                    <th key={h} className="px-4 py-2.5 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {b.rows.map((r) => (
                  <tr key={r.join("|")}>
                    {b.head.map((h, j) => (
                      <td
                        key={h || "first"}
                        className={cx("px-4 py-3 align-top", j === 0 && "font-medium")}
                      >
                        {r[j]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

export function BlogIndex() {
  usePageMeta(
    "The recoup blog",
    "Simple guides to getting money back from your vendors: reading the contract, meeting the deadline, getting paid.",
  );
  const [first, ...rest] = POSTS;
  return (
    <SiteLayout>
      <Wrap className="py-16">
        <Kicker>Blog</Kicker>
        <Display as="h1" className="mt-5 max-w-3xl">
          SLA credits, in plain words.
        </Display>
        {first && (
          <Link
            to={postPath(first)}
            className="group mt-12 block rounded-panel bg-band p-8 text-band-ink sm:p-10"
          >
            <p className="font-mono text-xs tracking-label text-band-muted uppercase">
              Start here · {first.minutes} min read
            </p>
            <h2 className="mt-4 max-w-3xl font-display text-3xl font-bold tracking-tight text-balance sm:text-4xl">
              {first.title}
            </h2>
            <p className="mt-3 max-w-2xl text-band-muted">{first.dek}</p>
            <span className="mt-6 inline-block font-semibold text-hl">Read it →</span>
          </Link>
        )}
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {rest.map((p) => (
            <Link
              key={p.slug}
              to={postPath(p)}
              className="group flex flex-col gap-3 rounded-panel border border-line bg-surface p-7 transition-colors hover:border-ink"
            >
              <PostMeta p={p} />
              <h2 className="font-display text-2xl font-bold tracking-tight text-balance group-hover:text-accent">
                {p.title}
              </h2>
              <p className="text-muted">{p.dek}</p>
            </Link>
          ))}
        </div>
      </Wrap>
      <Cta />
    </SiteLayout>
  );
}

export function PostPage() {
  const { slug } = useParams();
  const p = POSTS.find((x) => x.slug === slug);
  usePageMeta(p ? `${p.title} · recoup` : "recoup", p?.dek ?? "");
  if (!p) return <Navigate to="/resources" replace />;
  const more = POSTS.filter((x) => x.slug !== p.slug).slice(0, 2);
  return (
    <SiteLayout>
      <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <nav
          className="font-mono text-xs tracking-label text-faint uppercase"
          aria-label="Breadcrumb"
        >
          <Link to="/resources" className="hover:text-ink">
            Blog
          </Link>
        </nav>
        <h1 className="mt-5 font-display text-4xl leading-display font-bold tracking-tight text-balance sm:text-5xl">
          {p.title}
        </h1>
        <p className="mt-5 text-xl text-muted">{p.dek}</p>
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-y border-line py-4">
          <PostMeta p={p} />
          <ShareBar p={p} />
        </div>
        <div className="mt-10">
          <PostBody blocks={p.body} />
        </div>
        <div className="mt-14 rounded-panel bg-band p-8 text-band-ink">
          <h2 className="font-display text-2xl font-bold tracking-tight">
            See what your vendors owe you
          </h2>
          <p className="mt-2 text-band-muted">
            Start with a free audit: up to 5 contracts and the last 12 months, with the claims ready
            to send.
          </p>
          <Link
            to="/waitlist"
            className="mt-5 inline-flex h-11 items-center rounded-control bg-hl px-5 font-semibold text-hl-ink"
          >
            Get started
          </Link>
        </div>
        <div className="mt-10 border-t border-line pt-6">
          <ShareBar p={p} />
        </div>
      </article>
      <section className="border-t border-line bg-surface">
        <Wrap className="grid gap-4 py-16 md:grid-cols-2">
          {more.map((x) => (
            <Link
              key={x.slug}
              to={postPath(x)}
              className="group flex flex-col gap-2 rounded-panel border border-line bg-bg p-7 hover:border-ink"
            >
              <PostMeta p={x} />
              <h2 className="font-display text-xl font-bold tracking-tight group-hover:text-accent">
                {x.title}
              </h2>
              <p className="text-sm text-muted">{x.dek}</p>
            </Link>
          ))}
        </Wrap>
      </section>
    </SiteLayout>
  );
}
